import React, { useState, useEffect, useRef } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { useClienteDaProducao } from '../utils/clienteDaProducao';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

const BASE_URLS = [BASE_URL];

async function apiFetch(endpoint, options = {}) {
  const token = getAuthToken();
  if (token) {
    options = { ...options };
    options.headers = { ...options.headers, Authorization: options.headers?.Authorization || `Bearer ${token}` };
  }
  let lastError = null;
  for (const base of BASE_URLS) {
    try {
      const url = base + endpoint;
      const resp = await fetch(url, options);
      if (resp.ok || (resp.status >= 200 && resp.status < 300)) {
        const ct = resp.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { 
            return await resp.json(); 
          } catch { 
            return { success: true, status: resp.status }; 
          }
        }
        return await resp.text();
      } else {
        const errorText = await resp.text().catch(() => `Status ${resp.status}`);
        lastError = `${url} - ${errorText}`;
        console.error(`Erro na URL ${url}:`, errorText);
      }
    } catch (err) {
      lastError = `${base}${endpoint} - ${err.message}`;
      console.error(`Erro ao conectar com ${base}${endpoint}:`, err);
    }
  }
  throw new Error(`API indisponível para ${endpoint}. Último erro: ${lastError}`);
}

const Campo = ({ label, value }) => (
  <div style={{ minWidth: 160, padding: '8px 12px', background: 'linear-gradient(135deg, #f5f5f5, #fff)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    <div style={{ fontSize: 11, color: '#999', marginBottom: 4, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>{value || '—'}</div>
  </div>
);

export default function ServicosTerceirosDropdown() {
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filtro inicial pela URL (ex.: "Ver mais" da Gestão Financeira: ?filtroTipo=producaoId&filtroValor=ID)
  const [filtroTipo, setFiltroTipo] = useState(() => {
    const tipo = new URLSearchParams(window.location.search).get('filtroTipo');
    return ['descricao', 'status', 'fornecedor', 'producaoId'].includes(tipo) ? tipo : 'descricao';
  });
  const [filtroValor, setFiltroValor] = useState(() => new URLSearchParams(window.location.search).get('filtroValor') || '');
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [editarAberto, setEditarAberto] = useState(false);
  const [criarAberto, setCriarAberto] = useState(false);
  // Cancelar: como o formulário estava ao abrir (para saber se algo foi preenchido) e o pop-up de confirmação
  const formOriginalRef = useRef('');
  const [confirmarDescarte, setConfirmarDescarte] = useState(null); // 'criar' | 'editar' | null
  // Produção escolhida no "Adicionar" e serviços trazidos do orçamento dela (só na tela até clicar em Adicionar)
  const [producaoEscolhida, setProducaoEscolhida] = useState(null);
  const [servicosDoOrcamento, setServicosDoOrcamento] = useState([]);
  const [trazendoOrcamento, setTrazendoOrcamento] = useState(false);
  const [orcamentoPopupAberto, setOrcamentoPopupAberto] = useState(false);
  const [selecionarProducaoAberto, setSelecionarProducaoAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [producoes, setProducoes] = useState([]);
  const [filtroProducao, setFiltroProducao] = useState('');
  const [tipoFiltroProducao, setTipoFiltroProducao] = useState('todos');
  const [loadingProducoes, setLoadingProducoes] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [loadingFornecedores, setLoadingFornecedores] = useState(false);
  const [tiposServico, setTiposServico] = useState([]);
  const [loadingTiposServico, setLoadingTiposServico] = useState(false);

  const [form, setForm] = useState({
    descricao: '',
    valor: '',
    fornecedorId: '',
    producaoId: '',
    id_cliente: '',
    dataExecucao: '',
    status: 'Pendente',
    tipoServicoId: ''
  });

  // Cliente vem da produção (não é editável)
  useClienteDaProducao(form.producaoId, clienteId => setForm(prev => ({ ...prev, id_cliente: clienteId })));

  const opcoesStatus = ['Pendente', 'Em Andamento', 'Concluído', 'Cancelado'];

  useEffect(() => {
    carregarServicos();
    carregarProducoes();
    carregarFornecedores();
    carregarTiposServico();
  }, []);

  useEffect(() => {
    const handler = () => {
      setDetalheAberto(false);
      setEditarAberto(false);
      setCriarAberto(false);
      setSelecionarProducaoAberto(false);
      setSelecionado(null);
    };
    window.addEventListener('servicos-terceiros-fechar-todos-popups', handler);
    return () => window.removeEventListener('servicos-terceiros-fechar-todos-popups', handler);
  }, []);

  const carregarProducoes = async () => {
    try {
      setLoadingProducoes(true);
      const data = await apiFetch('/Producoes');
      const arr = Array.isArray(data) ? data : [];
      setProducoes(arr);
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
      setProducoes([]);
    } finally {
      setLoadingProducoes(false);
    }
  };

  const carregarFornecedores = async () => {
    try {
      setLoadingFornecedores(true);
      const data = await apiFetch('/Fornecedor');
      setFornecedores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar fornecedores:', err);
      setFornecedores([]);
    } finally {
      setLoadingFornecedores(false);
    }
  };

  const carregarTiposServico = async () => {
    try {
      setLoadingTiposServico(true);
      const data = await apiFetch('/TipoServico');
      setTiposServico(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar tipos de serviço:', err);
      setTiposServico([]);
    } finally {
      setLoadingTiposServico(false);
    }
  };

  const carregarServicos = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/ServicosTerceiros');
      const lista = (Array.isArray(data) ? data : [])
        .filter(st => (st.producaoId ?? st.ProducaoId) != null)
        .map(st => ({ ...st, dataExecucao: st.dataExecucao ?? st.dataEntrega ?? st.DataEntrega ?? '' }));
      setServicos(lista);
    } catch (err) {
      setError(err.message);
      setServicos([]);
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalhe = (servico) => {
    setSelecionado(servico);
    setDetalheAberto(true);
  };

  const abrirEditar = (servico) => {
    setSelecionado(servico);
    const novoForm = {
      descricao: servico.descricao || '',
      valor: servico.valor || '',
      fornecedorId: servico.fornecedorId || '',
      producaoId: servico.producaoId || '',
      id_cliente: servico.id_cliente || '',
      dataExecucao: servico.dataExecucao ? servico.dataExecucao.split('T')[0] : '',
      status: servico.status || 'Pendente',
      tipoServicoId: servico.tipoServicoId || ''
    };
    setForm(novoForm);
    formOriginalRef.current = camposPreenchidos(novoForm);
    setEditarAberto(true);
  };

  // Só os campos que o usuário preenche (o cliente vem da produção e muda sozinho)
  const camposPreenchidos = (f) =>
    JSON.stringify([f.descricao, f.valor, f.fornecedorId, f.tipoServicoId, f.dataExecucao, f.status].map(v => String(v ?? '')));

  const fecharFormulario = (tipo) => {
    if (tipo === 'criar') {
      setCriarAberto(false);
      setServicosDoOrcamento([]);
      setOrcamentoPopupAberto(false);
    } else {
      setEditarAberto(false);
    }
    setConfirmarDescarte(null);
  };

  // Cancelar: se algo foi preenchido/alterado, pergunta antes de fechar
  const pedirCancelar = (tipo) => {
    const alterou = camposPreenchidos(form) !== formOriginalRef.current
      || (tipo === 'criar' && servicosDoOrcamento.length > 0);
    if (alterou) setConfirmarDescarte(tipo);
    else fecharFormulario(tipo);
  };

  const selecionarProducaoEAbrir = (producao) => {
    const producaoId = producao.producaoId || producao.ProducaoId || producao.id;
    const clienteId = producao.clienteId || producao.ClienteId || producao.id_cliente;
    
    const novoForm = {
      descricao: '',
      valor: '',
      fornecedorId: '',
      producaoId: producaoId,
      id_cliente: clienteId || '',
      dataExecucao: '',
      status: 'Pendente',
      tipoServicoId: ''
    };
    setForm(novoForm);
    formOriginalRef.current = camposPreenchidos(novoForm);
    setProducaoEscolhida(producao);
    setServicosDoOrcamento([]);
    setOrcamentoPopupAberto(false);
    setSelecionarProducaoAberto(false);
    setFiltroProducao('');
    setCriarAberto(true);
  };

  const orcamentoDaProducao = (p) => p ? (p.orcamentoId ?? p.OrcamentoId ?? null) : null;

  // Traz os serviços terceiros do orçamento da produção para a tela, iguais ao orçamento (não salva nada ainda)
  const trazerServicosDoOrcamento = async () => {
    const orcamentoId = orcamentoDaProducao(producaoEscolhida);
    if (!orcamentoId) {
      alert('Este projeto não tem orçamento vinculado.');
      return;
    }
    try {
      setTrazendoOrcamento(true);
      const data = await apiFetch(`/ServicosTerceiros/orcamento/${orcamentoId}`);
      const doOrcamento = (Array.isArray(data) ? data : []).filter(st => (st.producaoId ?? st.ProducaoId) == null);
      if (doOrcamento.length === 0) {
        alert('O orçamento deste projeto não tem serviços terceiros.');
        return;
      }
      setServicosDoOrcamento(doOrcamento.map((st, i) => ({
        chave: `${st.id ?? st.Id ?? 'n'}-${i}-${Date.now()}`,
        descricao: st.descricao ?? st.Descricao ?? '',
        valor: st.valor ?? st.Valor ?? '',
        fornecedorId: st.fornecedorId ?? st.FornecedorId ?? '',
        tipoServicoId: st.tipoServicoId ?? st.TipoServicoId ?? '',
        dataExecucao: String(st.dataEntrega ?? st.DataEntrega ?? '').split('T')[0],
        status: 'Pendente'
      })));
      setOrcamentoPopupAberto(true);
    } catch (err) {
      alert('Erro ao trazer os serviços do orçamento: ' + err.message);
    } finally {
      setTrazendoOrcamento(false);
    }
  };

  const alterarServicoDoOrcamento = (chave, campo, valor) =>
    setServicosDoOrcamento(lista => lista.map(st => st.chave === chave ? { ...st, [campo]: valor } : st));

  const removerServicoDoOrcamento = (chave) =>
    setServicosDoOrcamento(lista => lista.filter(st => st.chave !== chave));

  // Salva os serviços trazidos do orçamento, um depois do outro, ligados à produção (sem orçamento)
  const adicionarServicosDoOrcamento = async () => {
    if (servicosDoOrcamento.length === 0) return;
    const semDescricao = servicosDoOrcamento.find(st => !String(st.descricao || '').trim());
    if (semDescricao) {
      alert('Todos os serviços precisam de descrição.');
      return;
    }
    let salvos = 0;
    try {
      setLoading(true);
      for (const st of servicosDoOrcamento) {
        await apiFetch('/ServicosTerceiros', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: 0,
            descricao: st.descricao,
            valor: parseFloat(st.valor) || 0,
            fornecedorId: parseInt(st.fornecedorId) || null,
            tipoServicoId: parseInt(st.tipoServicoId) || null,
            dataEntrega: st.dataExecucao || null,
            status: st.status || 'Pendente',
            producaoId: parseInt(form.producaoId) || null,
            id_cliente: parseInt(form.id_cliente) || null,
            orcamentoId: null
          })
        });
        salvos++;
      }
      alert(`${salvos} serviço(s) terceiro(s) adicionado(s) ao projeto.`);
      setServicosDoOrcamento([]);
      setOrcamentoPopupAberto(false);
      setCriarAberto(false);
      carregarServicos();
    } catch (err) {
      alert(`Erro ao adicionar (salvos: ${salvos} de ${servicosDoOrcamento.length}): ` + err.message);
      carregarServicos();
    } finally {
      setLoading(false);
    }
  };

  const producoesFiltradas = producoes.filter(p => {
    if (!filtroProducao.trim()) return true;
    const val = filtroProducao.toLowerCase();
    const id = (p.producaoId || p.ProducaoId || p.id || '').toString().toLowerCase();
    const contratoId = (p.contratosId || p.ContratosId || '').toString().toLowerCase();
    const oportunidadeId = (p.oportunidadeId || p.OportunidadeId || '').toString().toLowerCase();
    const titulo = (p.oportunidadeTitulo || p.OportunidadeTitulo || '').toLowerCase();
    const clienteId = (p.clienteId || p.ClienteId || p.id_cliente || '').toString().toLowerCase();
    const vendaId = (p.vendaId || p.VendaId || '').toString().toLowerCase();

    if (tipoFiltroProducao === 'todos') {
      return id.includes(val) || contratoId.includes(val) || oportunidadeId.includes(val) || titulo.includes(val) || clienteId.includes(val) || vendaId.includes(val);
    } else if (tipoFiltroProducao === 'producaoId') {
      return id.includes(val);
    } else if (tipoFiltroProducao === 'contratoId') {
      return contratoId.includes(val);
    } else if (tipoFiltroProducao === 'oportunidadeId') {
      return oportunidadeId.includes(val);
    } else if (tipoFiltroProducao === 'titulo') {
      return titulo.includes(val);
    } else if (tipoFiltroProducao === 'clienteId') {
      return clienteId.includes(val);
    } else if (tipoFiltroProducao === 'vendaId') {
      return vendaId.includes(val);
    }
    return true;
  });

  const salvarEdicao = async () => {
    if (!selecionado) return;
    try {
      setLoading(true);
      await apiFetch(`/ServicosTerceiros/${selecionado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selecionado.id,
          ...form,
          valor: parseFloat(form.valor) || 0,
          fornecedorId: parseInt(form.fornecedorId) || null,
          producaoId: parseInt(form.producaoId) || null,
          id_cliente: parseInt(form.id_cliente) || null,
          tipoServicoId: parseInt(form.tipoServicoId) || null,
          dataEntrega: form.dataExecucao || null,
          // Mantém o vínculo com o orçamento (sem isso o serviço era desligado do orçamento ao editar)
          orcamentoId: selecionado.orcamentoId ?? selecionado.OrcamentoId ?? null
        })
      });
      alert('Serviço atualizado com sucesso!');
      setEditarAberto(false);
      carregarServicos();
    } catch (err) {
      alert('Erro ao atualizar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const criarServico = async () => {
    if (!form.descricao) {
      alert('A descrição é obrigatória');
      return;
    }
    try {
      setLoading(true);
      await apiFetch('/ServicosTerceiros', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: 0,
          ...form,
          valor: parseFloat(form.valor) || 0,
          fornecedorId: parseInt(form.fornecedorId) || null,
          producaoId: parseInt(form.producaoId) || null,
          id_cliente: parseInt(form.id_cliente) || null,
          tipoServicoId: parseInt(form.tipoServicoId) || null,
          dataEntrega: form.dataExecucao || null,
          orcamentoId: null
        })
      });
      alert('Serviço criado com sucesso!');
      setCriarAberto(false);
      setForm({
        descricao: '',
        valor: '',
        fornecedorId: '',
        producaoId: '',
        id_cliente: '',
        dataExecucao: '',
        status: 'Pendente',
        tipoServicoId: ''
      });
      carregarServicos();
    } catch (err) {
      alert('Erro ao criar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filtradas = servicos.filter((s) => {
    if (!filtroValor.trim()) return true;
    const val = filtroValor.toLowerCase();
    switch (filtroTipo) {
      case 'descricao': return (s.descricao || '').toLowerCase().includes(val);
      case 'status': return (s.status || '').toLowerCase().includes(val);
      case 'fornecedor': return (s.fornecedorNome || '').toLowerCase().includes(val);
      case 'producaoId': return String(s.producaoId || '') === val.trim(); // ID exato (antes "1" também achava 10, 11...)
      default: return true;
    }
  });

  const totalValor = filtradas.reduce((acc, s) => acc + (parseFloat(s.valor) || 0), 0);

  return (
    <div className="dropdown-container">
      <div className="dropdown-header">
        <h2 style={{ margin: 0 }}>Serviços Terceiros</h2>
      </div>

      <MobileAddListener onOpen={() => setSelecionarProducaoAberto(true)} />

      {/* Botão Adicionar */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
        <button 
          className="btn-laranja-st"
          onClick={() => setSelecionarProducaoAberto(true)}
          style={{ 
            padding: '12px 24px', 
            backgroundColor: 'rgb(235, 133, 0)', 
            color: 'white', 
            border: 'none', 
            borderRadius: 6, 
            cursor: 'pointer', 
            fontWeight: 600,
            fontSize: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
          }}
        >
          Adicionar
        </button>
      </div>

      {/* Filtros */}
      <div className="dropdown-filter-row" style={{ display: 'flex', gap: 10, padding: '10px 20px', borderBottom: '1px solid #ddd', background: '#fafafa' }}>
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}>
          <option value="descricao">Descrição</option>
          <option value="status">Status</option>
          <option value="fornecedor">Fornecedor</option>
          <option value="producaoId">Projeto ID</option>
        </select>
        <input
          type="text"
          placeholder="Buscar..."
          value={filtroValor}
          onChange={(e) => setFiltroValor(e.target.value)}
          style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}
        />
      </div>

      {/* Estatísticas */}
      <div style={{ padding: '15px 20px', background: 'linear-gradient(135deg, rgb(235, 133, 0), #ffd500)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Total de Serviços</div>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>{filtradas.length}</div>
        </div>
        <div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Valor Total</div>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>
            {totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </div>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Carregando...</div>}
      {error && <div style={{ textAlign: 'center', padding: 40, color: 'red' }}>Erro: {error}</div>}

      {/* Lista */}
      {!loading && !error && (
        <div className="servicos-terceiros-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 20 }}>
          {(!filtradas || filtradas.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhum serviço encontrado para o filtro' : 'Nenhum serviço cadastrado'}
            </div>
          ) : (
            filtradas.map((s, idx) => {
              const statusColor = s.status === 'Concluído' ? '#4caf50' : s.status === 'Em Andamento' ? '#2196f3' : s.status === 'Cancelado' ? '#f44336' : 'rgb(235, 133, 0)';
              return (
                <div className="servico-terceiro-card" key={s.id || idx} style={{
                  background: 'white',
                  borderRadius: 14,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  border: `2.5px solid ${statusColor}`,
                  padding: 32,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}>
                  <div className="servico-terceiro-card-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                    <div className="servico-terceiro-card-header" style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      marginBottom: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        background: statusColor,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 13
                      }}>
                        {s.status || 'Pendente'}
                      </span>
                    </div>
                    
                    <div className="servico-terceiro-card-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                      <div className="servico-terceiro-field servico-terceiro-field-id">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>ID:</span>{' '}
                        <span className="servico-terceiro-field-value">#{s.id}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-descricao">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Descrição:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.descricao}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-producao">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Projeto:</span>{' '}
                        <span className="servico-terceiro-field-value">#{s.producaoId}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-cliente">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Cliente ID:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.id_cliente}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-fornecedor-id">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Fornecedor ID:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.fornecedorId}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-fornecedor">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Fornecedor:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.fornecedorNome}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-tipo">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Tipo de Serviço:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.tipoServicoNome ?? s.tipoServico}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-valor">
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Valor:</span>{' '}
                        <span className="servico-terceiro-field-value" style={{ color: '#4CAF50', fontWeight: '500' }}>{s.valor ? `R$ ${parseFloat(s.valor).toFixed(2)}` : '—'}</span>
                      </div>

                      <div className="servico-terceiro-field servico-terceiro-field-data" style={{ gridColumn: 'span 2' }}>
                        <span className="servico-terceiro-field-label" style={{ color: '#666', fontWeight: '600' }}>Data Execução:</span>{' '}
                        <span className="servico-terceiro-field-value">{s.dataExecucao ? new Date(s.dataExecucao).toLocaleDateString('pt-BR') : '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="servico-terceiro-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                    <button 
                      className="btn-detalhe-servico-terceiro"
                      onClick={() => abrirDetalhe(s)}
                      style={{ 
                        background: '#2196F3', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 7, 
                        padding: '10px 24px', 
                        fontWeight: 600, 
                        fontSize: 16, 
                        minWidth: 120, 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Ver Detalhes
                    </button>
                    <button 
                      className="btn-editar-servico-terceiro"
                      onClick={() => abrirEditar(s)}
                      style={{ 
                        background: 'rgb(235, 133, 0)', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 7, 
                        padding: '10px 24px', 
                        fontWeight: 600, 
                        fontSize: 16, 
                        minWidth: 120, 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Editar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal Detalhe */}
      {detalheAberto && selecionado && (
        <div className="modal-overlay" onClick={() => setDetalheAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Serviço Terceiro - Detalhes</h3>
              <button onClick={() => setDetalheAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-servicos-terceiros-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                <input type="text" readOnly value={selecionado.id ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Cliente ID</label>
                <input type="text" readOnly value={selecionado.id_cliente ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Projeto ID</label>
                <input type="text" readOnly value={selecionado.producaoId ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Fornecedor ID</label>
                <input type="text" readOnly value={selecionado.fornecedorId ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Fornecedor Nome</label>
                <input type="text" readOnly value={selecionado.fornecedorNome ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo de Serviço</label>
                <input type="text" readOnly value={selecionado.tipoServicoNome ?? selecionado.tipoServico ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 3' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição</label>
                <textarea readOnly value={selecionado.descricao ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white', minHeight: 80, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Valor</label>
                <input type="text" readOnly value={selecionado.valor ? `R$ ${parseFloat(selecionado.valor).toFixed(2)}` : ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Execução</label>
                <input type="text" readOnly value={selecionado.dataExecucao ? new Date(selecionado.dataExecucao).toLocaleDateString('pt-BR') : ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Status</label>
                <input type="text" readOnly value={selecionado.status ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selecionar Projeto */}
      {selecionarProducaoAberto && (
        <div className="modal-overlay" onClick={() => setSelecionarProducaoAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '95%', maxWidth: 1000, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>📋 Selecione um Projeto</h3>
              <button onClick={() => setSelecionarProducaoAberto(false)} aria-label="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            {/* Filtro de Projeto - idêntico ao de Tarefas */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                placeholder={`Filtrar por ${
                  tipoFiltroProducao === 'todos' ? 'todos os campos' :
                  tipoFiltroProducao === 'producaoId' ? 'ID Projeto' :
                  tipoFiltroProducao === 'contratoId' ? 'Contrato ID' :
                  tipoFiltroProducao === 'oportunidadeId' ? 'Oportunidade ID' :
                  tipoFiltroProducao === 'titulo' ? 'Título' :
                  tipoFiltroProducao === 'clienteId' ? 'Cliente ID' :
                  tipoFiltroProducao === 'vendaId' ? 'Venda ID' : ''
                }...`}
                value={filtroProducao}
                onChange={(e) => setFiltroProducao(e.target.value)}
                style={{ flex: 1, padding: '10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
              />
              <select
                value={tipoFiltroProducao}
                onChange={(e) => setTipoFiltroProducao(e.target.value)}
                style={{ 
                  padding: '10px 16px', 
                  borderRadius: 6, 
                  border: '1px solid #ddd', 
                  fontSize: 14, 
                  backgroundColor: 'rgb(235, 133, 0)', 
                  color: 'white', 
                  cursor: 'pointer',
                  fontWeight: 600,
                  minWidth: 150
                }}
              >
                <option value="todos">Filtrar por: Todos</option>
                <option value="producaoId">ID Projeto</option>
                <option value="contratoId">Contrato ID</option>
                <option value="oportunidadeId">Oportunidade ID</option>
                <option value="titulo">Título</option>
                <option value="clienteId">Cliente ID</option>
                <option value="vendaId">Venda ID</option>
              </select>
            </div>

            {loadingProducoes ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos...</div>
            ) : producoesFiltradas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Nenhum projeto encontrado</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {producoesFiltradas.map((p, idx) => {
                  const producaoId = p.producaoId || p.ProducaoId || p.id;
                  const contratoId = p.contratosId || p.ContratosId;
                  const oportunidadeId = p.oportunidadeId || p.OportunidadeId;
                  const titulo = p.oportunidadeTitulo || p.OportunidadeTitulo || 'Sem título';
                  const status = p.status || p.Status || 'N/A';
                  const clienteId = p.clienteId || p.ClienteId || p.id_cliente;
                  const vendaId = p.vendaId || p.VendaId;

                  return (
                    <div
                      key={producaoId || idx}
                      onClick={() => selecionarProducaoEAbrir(p)}
                      style={{
                        padding: 16,
                        border: '2px solid #e0e0e0',
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#fafafa'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgb(235, 133, 0)';
                        e.currentTarget.style.backgroundColor = '#fff8f0';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.backgroundColor = '#fafafa';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'rgb(235, 133, 0)' }}>ID: {producaoId}</span>
                          <span style={{ 
                            fontSize: 11, 
                            padding: '2px 8px', 
                            borderRadius: 10, 
                            backgroundColor: status === 'Concluído' ? '#e6f4ea' : status === 'Em Andamento' ? '#e3f2fd' : '#fff8e1',
                            color: status === 'Concluído' ? '#1e8e3e' : status === 'Em Andamento' ? '#1565c0' : '#f9a825',
                            fontWeight: 600
                          }}>
                            {status}
                          </span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{titulo}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
                          <div><strong>Contrato ID:</strong> {contratoId || 'N/A'}</div>
                          <div><strong>Oportunidade ID:</strong> {oportunidadeId || 'N/A'}</div>
                          <div><strong>Cliente ID:</strong> {clienteId || 'N/A'}</div>
                          <div><strong>Venda ID:</strong> {vendaId || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Criar */}
      {criarAberto && (
        <div className="modal-overlay" onClick={() => setCriarAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Adicionar Serviço Terceiro</h3>
              <button
                type="button"
                className="btn-laranja-st"
                onClick={trazerServicosDoOrcamento}
                disabled={trazendoOrcamento || !orcamentoDaProducao(producaoEscolhida)}
                title={orcamentoDaProducao(producaoEscolhida) ? 'Traz os serviços terceiros do orçamento deste projeto para você revisar' : 'Este projeto não tem orçamento vinculado'}
                style={{ marginLeft: 'auto', marginRight: 12, padding: '8px 14px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: orcamentoDaProducao(producaoEscolhida) ? 1 : 0.5 }}
              >
                {trazendoOrcamento ? 'Buscando...' : '📋 Trazer serviços do orçamento'}
              </button>
              <button onClick={() => setCriarAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-servicos-terceiros-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Cliente ID</label>
                <input type="number" value={form.id_cliente} readOnly title="O cliente vem do projeto" style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }} placeholder="Preenchido pelo projeto" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Projeto ID</label>
                <input type="number" value={form.producaoId} readOnly style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5' }} placeholder="ID do projeto" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Fornecedor</label>
                <select
                  value={form.fornecedorId}
                  onChange={(e) => setForm({ ...form, fornecedorId: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um fornecedor</option>
                  {fornecedores.map(f => (
                    <option key={f.id || f.fornecedorId} value={f.id || f.fornecedorId}>
                      {f.nomeFornecedor || f.nome || f.razaoSocial || `Fornecedor ${(f.id || f.fornecedorId)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo de Serviço</label>
                <select
                  value={form.tipoServicoId}
                  onChange={(e) => setForm({ ...form, tipoServicoId: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um tipo</option>
                  {tiposServico.map(ts => (
                    <option key={ts.id} value={ts.id}>
                      {ts.descricaoTpServico || `Tipo ${ts.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Valor</label>
                <input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} placeholder="0.00" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Execução</label>
                <input type="date" value={form.dataExecucao} onChange={(e) => setForm({ ...form, dataExecucao: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}>
                  {opcoesStatus.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 3' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição *</label>
                <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', minHeight: 80, resize: 'vertical' }} placeholder="Descrição do serviço" />
              </div>
            </div>
            {orcamentoPopupAberto && (
              <div onClick={() => setOrcamentoPopupAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95010, padding: 16 }}>
              <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, maxHeight: '88vh', overflowY: 'auto', padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                  <div>
                    <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>📋 Serviços terceiros do orçamento</h3>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      Orçamento #{orcamentoDaProducao(producaoEscolhida)} · Projeto #{form.producaoId}. Revise, altere ou tire com o × os que não quiser. Nada é salvo até clicar em Adicionar.
                    </div>
                  </div>
                  <button type="button" onClick={() => setOrcamentoPopupAberto(false)} aria-label="Fechar" title="Fechar e voltar ao formulário" style={{ flexShrink: 0, width: 36, height: 36, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: 'rgb(235, 133, 0)', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
                </div>
                {servicosDoOrcamento.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#777' }}>Você tirou todos os serviços. Feche para adicionar manualmente ou traga do orçamento de novo.</div>
                )}
                <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                  {servicosDoOrcamento.map(st => (
                    <div key={st.chave} style={{ position: 'relative', padding: 12, paddingRight: 44, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      <button
                        type="button"
                        onClick={() => removerServicoDoOrcamento(st.chave)}
                        title="Tirar este serviço"
                        style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: '#f44336', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                      >
                        ×
                      </button>
                      <div className="formulario-servicos-terceiros-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                        <select value={st.fornecedorId} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'fornecedorId', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: '1px solid #ddd' }} title="Fornecedor">
                          <option value="">Fornecedor</option>
                          {fornecedores.map(f => (
                            <option key={f.id || f.fornecedorId} value={f.id || f.fornecedorId}>
                              {f.nomeFornecedor || f.nome || f.razaoSocial || `Fornecedor ${(f.id || f.fornecedorId)}`}
                            </option>
                          ))}
                        </select>
                        <select value={st.tipoServicoId} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'tipoServicoId', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: '1px solid #ddd' }} title="Tipo de serviço">
                          <option value="">Tipo de serviço</option>
                          {tiposServico.map(ts => <option key={ts.id} value={ts.id}>{ts.descricaoTpServico || `Tipo ${ts.id}`}</option>)}
                        </select>
                        <input type="number" step="0.01" value={st.valor} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'valor', e.target.value)} placeholder="Valor" title="Valor" style={{ padding: '8px', borderRadius: 6, border: '1px solid #ddd' }} />
                        <input type="date" value={st.dataExecucao} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'dataExecucao', e.target.value)} title="Data" style={{ padding: '8px', borderRadius: 6, border: '1px solid #ddd' }} />
                        <select value={st.status} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'status', e.target.value)} title="Status" style={{ padding: '8px', borderRadius: 6, border: '1px solid #ddd' }}>
                          {opcoesStatus.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        <textarea value={st.descricao} onChange={(e) => alterarServicoDoOrcamento(st.chave, 'descricao', e.target.value)} placeholder="Descrição" title="Descrição" style={{ gridColumn: '1 / -1', padding: '8px', borderRadius: 6, border: '1px solid #ddd', minHeight: 50, resize: 'vertical' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <button type="button" className="btn-laranja-st" onClick={adicionarServicosDoOrcamento} disabled={servicosDoOrcamento.length === 0} style={{ padding: '10px 24px', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                    Adicionar {servicosDoOrcamento.length} serviço(s) do orçamento
                  </button>
                </div>
              </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20 }}>
              <button type="button" className="btn-cancelar-laranja" onClick={() => pedirCancelar('criar')} style={{ padding: '12px 30px', borderRadius: 6, cursor: 'pointer' }}>Cancelar</button>
              <button className="btn-laranja-st" onClick={criarServico} style={{ padding: '12px 30px', borderRadius: 6, border: 'none', background: 'rgb(235, 133, 0)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {confirmarDescarte && (
        <div onClick={() => setConfirmarDescarte(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95100, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 10px', color: 'rgb(235, 133, 0)' }}>Descartar o que foi preenchido?</h3>
            <p style={{ margin: '0 0 18px', color: '#444', fontSize: 14, lineHeight: 1.5 }}>
              Você vai perder todos os dados preenchidos neste formulário. Deseja prosseguir?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn-cancelar-laranja" onClick={() => setConfirmarDescarte(null)} style={{ padding: '10px 18px', borderRadius: 6, cursor: 'pointer' }}>Continuar editando</button>
              <button type="button" className="btn-laranja-st" onClick={() => fecharFormulario(confirmarDescarte)} style={{ padding: '10px 18px', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer' }}>Sim, descartar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {editarAberto && selecionado && (
        <div className="modal-overlay" onClick={() => setEditarAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Editar Serviço Terceiro</h3>
              <button onClick={() => setEditarAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-servicos-terceiros-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                <input type="text" readOnly value={selecionado.id ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Cliente ID</label>
                <input type="number" value={form.id_cliente} readOnly title="O cliente vem do projeto" style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Projeto ID</label>
                <input type="number" value={form.producaoId} onChange={(e) => setForm({ ...form, producaoId: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Fornecedor</label>
                <select
                  value={form.fornecedorId}
                  onChange={(e) => setForm({ ...form, fornecedorId: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um fornecedor</option>
                  {fornecedores.map(f => (
                    <option key={f.id || f.fornecedorId} value={f.id || f.fornecedorId}>
                      {f.nomeFornecedor || f.nome || f.razaoSocial || `Fornecedor ${(f.id || f.fornecedorId)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo de Serviço</label>
                <select
                  value={form.tipoServicoId}
                  onChange={(e) => setForm({ ...form, tipoServicoId: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um tipo</option>
                  {tiposServico.map(ts => (
                    <option key={ts.id} value={ts.id}>
                      {ts.descricaoTpServico || `Tipo ${ts.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Valor</label>
                <input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Execução</label>
                <input type="date" value={form.dataExecucao} onChange={(e) => setForm({ ...form, dataExecucao: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 3' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição</label>
                <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', minHeight: 80, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}>
                  {opcoesStatus.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button type="button" className="btn-cancelar-laranja" onClick={() => pedirCancelar('editar')} style={{ padding: '10px 20px', borderRadius: 6, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={salvarEdicao} style={{ padding: '10px 20px', borderRadius: 6, border: 'none', background: 'rgb(235, 133, 0)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
